/**
 * FundraiserBuilder.tsx - Multi-Step Fundraiser Creation/Editing Interface
 *
 * This is the main builder component that provides a step-by-step interface for creating
 * and editing fundraisers. It manages the builder workflow, validation, and data persistence.
 *
 * Key Features:
 * - 4-step builder process (Details, Donation, Design, Promoters)
 * - Form validation and error handling
 * - Unsaved changes detection and warnings
 * - Auto-save and manual save functionality
 * - Delete fundraiser with confirmation
 * - Responsive sticky header with scroll detection
 * - Step navigation and progress tracking
 *
 * Props:
 * - fundraiserId?: string - ID of fundraiser to edit (undefined for new)
 *
 * Dependencies: MobX store, step components, validation utilities, styled-components
 */

import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import {
  ArrowLeft,
  ArrowRight,
  Save,
  Eye,
  X,
  RotateCcw,
  Heart,
} from "lucide-react";
import { fundraiserStore } from "../stores/FundraiserStore";
import { getPrimaryFundraiserImage } from "../utils/fundraiserUtils";
import { RequiredFieldValidator } from "../components/shared/RequiredFieldValidator";
import { DetailsStep } from "./fundraiser-builder/DetailsStep";
import { DonationStep } from "./fundraiser-builder/DonationStep";
import { DesignStep } from "./fundraiser-builder/DesignStep";
import PromotersStep from "./fundraiser-builder/PromotersStep";
import { Toast } from "./Toast";
import { firstValueFrom } from "rxjs";
import { FundraiserService } from "../services";
import { Fundraiser } from "../models/FundraisersGridModels";
import {
  AuthService,
  Loader,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import {
  DonationAmountSelection,
  LegalTermsAndConditionType,
  MediaType,
} from "../enums";
import FullLibraryView from "./FullLibraryView";
import {
  ConfigurationStore,
  MediaItem,
  MediaService,
} from "@mindmemobile/mm-shared-library";
import { MediaModel } from "../models/GalleryModels";
import {
  BuilderSectionId,
  CategoryModel,
  CreateFundraiserModel,
  PromoterProgressModel,
} from "../models";
import { NavigateFunction } from "react-router-dom";
import ToastService from "./ToastService";
import { ApplicationSubAccountBusinessModel } from "@mindmemobile/mm-payment-modules";
import { canManageFundraiser } from "../utils/permissions";

const BuilderContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(
    135deg,
    #667eea 0%,
    #764ba2 25%,
    #f093fb 50%,
    #f5576c 75%,
    #4facfe 100%
  );
  position: relative;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(248, 250, 252, 0.95) 100%
    );
    pointer-events: none;
  }
`;

const HeaderContainer = styled.div<{ isScrolled: boolean }>`
  position: sticky;
  top: 0;
  z-index: 100;
  background: ${(props) =>
    props.isScrolled
      ? "linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(248, 250, 252, 0.95) 100%)"
      : "transparent"};
  backdrop-filter: ${(props) => (props.isScrolled ? "blur(20px)" : "none")};
  border-bottom: ${(props) =>
    props.isScrolled ? "1px solid rgba(255, 255, 255, 0.3)" : "none"};
  box-shadow: ${(props) =>
    props.isScrolled ? "0 4px 20px rgba(0, 0, 0, 0.1)" : "none"};
  padding: ${(props) => (props.isScrolled ? "16px 0" : "32px 0")};
  transition: all 0.3s ease;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 32px;
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  padding: 0 32px 80px;
  min-height: 100vh;
  max-width: 1400px;
  margin: 0 auto;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 12px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);

  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: translateY(-1px);
  }
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button<{ variant?: "primary" | "secondary" }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  backdrop-filter: blur(12px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);

  ${(props) =>
    props.variant === "primary"
      ? `
    background: linear-gradient(135deg, #10b981, #059669);
    color: #ffffff;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4);
    }
  `
      : `
    background: rgba(255, 255, 255, 0.9);
    color: #64748b;
    border: 1px solid rgba(255, 255, 255, 0.3);

    &:hover {
      background: rgba(255, 255, 255, 1);
      color: #1e293b;
      transform: translateY(-1px);
    }
  `}

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 40px;
`;

const StepWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
`;

const Step = styled.div<{
  active: boolean;
  completed: boolean;
  clickable: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  border-radius: 12px;
  background: transparent;
  border: none;
  transition: all 0.3s ease;
  cursor: ${(props) => (props.clickable ? "pointer" : "default")};
  box-shadow: none;
`;

const StepNumber = styled.div<{ active: boolean; completed: boolean }>`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 18px;
  flex-shrink: 0;
  background: ${(props) =>
    props.active
      ? "#152837"
      : props.completed
        ? "#132837"
        : "linear-gradient(135deg, #f1f5f9, #e2e8f0)"};
  opacity: ${(props) => (props.completed && !props.active ? 0.65 : 1)};
  color: ${(props) =>
    props.active || props.completed ? "#ffffff" : "#333333"};
`;

const StepLabel = styled.div<{ active: boolean; completed: boolean }>`
  font-size: 20px;
  font-weight: 600;
  color: ${(props) =>
    props.active ? "#152837" : props.completed ? "#64748b" : "#64748b"};
`;

const StepHoverBackground = styled.div`
  background: transparent; /* Removed hover background */
`;

const StepConnector = styled.div<{ completed: boolean }>`
  width: 40px;
  height: 2px;
  background: ${(props) => (props.completed ? "#132837" : "#9ca3af")};
  border-radius: 1px;
  transition: all 0.3s ease;
`;

const Modal = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 24px;
  padding: 40px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #dc2626;
  margin: 0;
  flex: 1;
  text-align: center;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;

  &:hover {
    background: #e2e8f0;
    transform: scale(1.05);
  }
`;

const ModalText = styled.p`
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 24px;
  font-size: 16px;
`;

const WarningText = styled.div`
  background: #fef2f2;
  border: 1px solid #fca5a5;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
  color: #dc2626;
  font-weight: 600;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const FormInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;

  &:focus {
    outline: none;
    border-color: #dc2626;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  }
`;

const DeleteConfirmInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${(props) => (props.hasError ? "#dc2626" : "#e2e8f0")};
  border-radius: 8px;
  font-size: 16px;
  color: #1e293b;
  background-color: ${(props) => (props.hasError ? "#fef2f2" : "#ffffff")};

  &:focus {
    outline: none;
    border-color: ${(props) => (props.hasError ? "#dc2626" : "#3b82f6")};
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.hasError ? "rgba(220, 38, 38, 0.1)" : "rgba(59, 130, 246, 0.1)"};
  }
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin: 0;
  flex-shrink: 0;
  margin-top: 2px;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const ModalButton = styled.button<{ variant?: "danger" }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${(props) =>
    props.variant === "danger"
      ? `
    background: #dc2626;
    color: #ffffff;

    &:hover {
      background: #b91c1c;
    }

    &:disabled {
      background: #9ca3af;
      cursor: not-allowed;
    }
  `
      : `
    background: #f1f5f9;
    color: #64748b;

    &:hover {
      background: #e2e8f0;
    }
  `}
`;

const FormCard = styled.div`
  position: relative;
  z-index: 2;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 40px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.3);
  margin-bottom: 32px;
  margin-top: 15px;
`;

const Navigation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 40px;
`;

const NavButton = styled.button<{ variant?: "primary" }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${(props) =>
    props.variant === "primary"
      ? `
    background: linear-gradient(135deg, #10b981, #059669);
    color: #ffffff;
    box-shadow: 0 4px 16px rgba(16, 185, 129, 0.3);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(16, 185, 129, 0.4);
    }
  `
      : `
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(12px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    color: #64748b;
    border: 1px solid rgba(255, 255, 255, 0.3);

    &:hover {
      background: rgba(255, 255, 255, 1);
      color: #1e293b;
      transform: translateY(-1px);
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const DeleteLink = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  background: #dc2626;
  color: #ffffff;
  box-shadow: 0 4px 16px rgba(220, 38, 38, 0.3);

  &:hover {
    background: #b91c1c;
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(220, 38, 38, 0.4);
  }

  &:active {
    transform: translateY(0);
    background: #991b1b;
  }
`;

const NavigationLeftSection = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
`;

const CenterSection = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  gap: 12px;
`;

const RightSection = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const UnsavedChangesModal = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 9999999;
  backdrop-filter: blur(4px);
`;

const UnsavedChangesContent = styled.div`
  background: #ffffff;
  border-radius: 24px;
  padding: 40px;
  width: 90%;
  max-width: 450px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const UnsavedChangesHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const UnsavedChangesTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
  flex: 1;
  text-align: center;
`;

const UnsavedChangesCloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;

  &:hover {
    background: #e2e8f0;
    transform: scale(1.05);
  }
`;

const UnsavedChangesText = styled.p`
  color: #64748b;
  line-height: 1.6;
  margin-bottom: 24px;
  font-size: 16px;
  text-align: center;
`;

const UnsavedChangesActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
`;

const UnsavedChangesActionsVertical = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
`;

const UnsavedChangesButton = styled.button<{
  variant?: "primary" | "secondary" | "danger" | "warning";
}>`
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;

  ${(props) => {
    switch (props.variant) {
      case "primary":
        return `
          background: #10b981;
          color: #ffffff;
          &:hover {
            background: #059669;
          }
        `;
      case "secondary":
        return `
          background: #f1f5f9;
          color: #64748b;
          &:hover {
            background: #e2e8f0;
          }
        `;
      case "warning":
        return `
          background: #f59e0b;
          color: #ffffff;
          &:hover {
            background: #d97706;
          }
        `;
      case "danger":
        return `
          background: #dc2626;
          color: #ffffff;
          &:hover {
            background: #b91c1c;
          }
        `;
      default:
        return `
          background: #f1f5f9;
          color: #64748b;
          &:hover {
            background: #e2e8f0;
          }
        `;
    }
  }}
`;

const GlobalActionButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: none;
  color: ${(props) => (props.disabled ? "#9ca3af" : "#152837")};
  font-size: 14px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s ease;
  text-decoration: none;

  &:hover:not(:disabled) {
    color: rgba(21, 40, 55, 0.8);
    text-decoration: underline;
  }

  &:active:not(:disabled) {
    color: rgba(21, 40, 55, 0.9);
  }
`;

const steps = [
  { id: 1, label: "Details" },
  { id: 2, label: "Donation" },
  { id: 3, label: "Design" },
  { id: 4, label: "Promoters" },
];

interface FundraiserBuilderProps {
  fundraiserId?: string;
  navigate: NavigateFunction;
}

export type LibraryField =
  | "showMainSectionLibrary"
  | "showMediaSectionLibrary"
  | "showCallToActionSectionLibrary"
  | "showMarketingSectionLibrary";

const FundraiserBuilder: React.FC<FundraiserBuilderProps> = observer(
  ({ fundraiserId, navigate }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [isEditing] = useState(!!fundraiserId);
    const [isScrolled, setIsScrolled] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmName, setDeleteConfirmName] = useState("");
    const [deleteAgreement, setDeleteAgreement] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showLoader, setShowLoader] = useState(false);
    const [showUnsavedChangesModal, setShowUnsavedChangesModal] =
      useState(false);

    const isInitialized = useRef(false);

    const hasPayments = false; // Placeholder until payment integration

    // Scroll detection for sticky header
    useEffect(() => {
      fundraiserStore.setDonations([]);
      const handleScroll = () => {
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        setIsScrolled(scrollTop > 50);
      };

      window.addEventListener("scroll", handleScroll, { passive: true });

      return () => {
        window.removeEventListener("scroll", handleScroll);
      };
    }, []);

    useEffect(() => {
      // Each builder session applies the merchant account's default surcharge settings
      // once, so clear the marker from any previously opened fundraiser.
      fundraiserStore.surchargeDefaultsAppliedForMerchantId = null;
      if (fundraiserId) {
        fetchFundraiserData();
        fetchPromoters();
      }
      if (!isEditing) {
        FundraiserService.getAccountBusinessInfo(
          AuthService.authData.accountId || ""
        ).subscribe((response) => {
          fundraiserStore.setSubAccountBusinessInfo(
            ApplicationSubAccountBusinessModel.deserializeAPIModel(
              response.result
            )
          );
          fundraiserStore.setBussinessAddressInBuilder(
            AuthService.shortUniqueId
          );
        });
      }
      fundraiserStore.fetchDonationItems();
      fundraiserStore.fetchMerchantAccounts("");
      fundraiserStore.fetchListInterestTags();
      fundraiserStore.fetchMindMeContactReferences();
      fundraiserStore.fetchMindMeContacts();
      fundraiserStore.fetchMobilePages();
    }, []);

    const fetchPromoters = async () => {
      const response = await firstValueFrom(
        FundraiserService.getPromoters(
          AuthService.authData.accountId,
          fundraiserId || "",
          "",
          1,
          99999
        )
      );
      if (response.result) {
        const data = response.result.map((p) =>
          PromoterProgressModel.deserializeAPIModel(p, fundraiserId || "")
        );
        fundraiserStore.setPromoters(data);
      }
    };

    const fetchFundraiserData = async () => {
      setShowLoader(true);
      try {
        const response = await firstValueFrom(
          FundraiserService.getFundraiserData(fundraiserId || "", "")
        );
        fundraiserStore.resetBuilder(AuthService.shortUniqueId);
        if (response.result) {
          const data = CreateFundraiserModel.deserializeAPIModel(
            response.result
          );
          fundraiserStore.setBuilderData(data);
          fundraiserStore.setFundraiserDetails(data);
          fundraiserStore.initializeBuilder(data);
          // A saved fundraiser keeps its own surcharge settings; the merchant account
          // defaults only kick in once the user picks a different account.
          fundraiserStore.surchargeDefaultsAppliedForMerchantId =
            data.merchantAccount?.id || null;
          setShowLoader(false);
        } else {
          ToastService.showError("Failed to load fundraiser data.");
          setShowLoader(false);
          navigate("/fundraiser/dashboard");
        }
      } catch (error) {
        console.error("Failed to load fundraisers", error);
      }
    };

    // Check if there are unsaved changes
    const hasUnsavedChanges = () => {
      const current = fundraiserStore.builderData;
      const saved = fundraiserStore.savedBuilderData;

      // Compare key fields that indicate changes
      const fieldsToCompare = [
        "name",
        "title",
        "subtitle",
        "goal",
        "startDate",
        "endDate",
        "missionTitle",
        "missionDescription",
        "iconImageChoice",
        "titleIcon",
        "titleColor",
        "donationFormType",
        "merchantAccount",
      ];

      for (const field of fieldsToCompare) {
        if (JSON.stringify(current[field]) !== JSON.stringify(saved[field])) {
          return true;
        }
      }

      // Compare arrays
      const arrayFieldsToCompare = [
        "mediaItems",
        "impactHighlights",
        "promoters",
        "fundraiserStats",
      ];
      for (const field of arrayFieldsToCompare) {
        if (
          JSON.stringify(current[field] || []) !==
          JSON.stringify(saved[field] || [])
        ) {
          return true;
        }
      }

      return false;
    };

    const handleBackClick = () => {
      if (hasUnsavedChanges()) {
        setShowUnsavedChangesModal(true);
      } else {
        // If editing existing campaign, go to details; otherwise go to dashboard
        if (fundraiserId) {
          // fundraiserStore.setView("fundraiser-details");
          navigate("/fundraiser/fundraiser-details");
        } else {
          // fundraiserStore.setView("dashboard");
          navigate("/fundraiser/dashboard");
        }
      }
    };

    const handleUnsavedChangesEdit = () => {
      setShowUnsavedChangesModal(false);
      // Stay on the current page
    };

    const handleUnsavedChangesSave = async () => {
      // Use the reusable validator
      const validationPassed = fundraiserStore.builderData.validate(
        false,
        fundraiserStore.fundraisers,
        setCurrentStep,
        navigateToSection
      );
      if (!validationPassed) {
        setShowUnsavedChangesModal(false);
        fundraiserStore.setBuilderData(fundraiserStore.builderData);
        return;
      }

      if (!canManageFundraiser(fundraiserId ? "Edit" : "Create")) return;
      setIsSaving(true);
      const success = await fundraiserStore.saveBuilder();
      setIsSaving(false);

      if (success) {
        setShowUnsavedChangesModal(false);
        if (fundraiserId) {
          // For editing existing fundraiser, navigate to details page
          fundraiserStore.setSelectedFundraiser(fundraiserId);
          navigate("/fundraiser/fundraiser-details");
        } else {
          // For new fundraiser, navigate to dashboard
          // fundraiserStore.setView("dashboard");
          navigate("/fundraiser/dashboard?created=true");
        }
      }
    };

    const handleUnsavedChangesContinue = () => {
      setShowUnsavedChangesModal(false);
      // If editing existing campaign, go to details; otherwise go to dashboard
      if (fundraiserId) {
        // fundraiserStore.setView("fundraiser-details");
        navigate("/fundraiser/fundraiser-details");
      } else {
        // fundraiserStore.setView("dashboard");
        navigate("/fundraiser/dashboard?created=true");
      }
    };

    const closeUnsavedChangesModal = () => {
      setShowUnsavedChangesModal(false);
    };

    const handleNext = () => {
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
        fundraiserStore.currentBuilderStep = currentStep + 1;
        // Scroll to top when navigating to next step
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    };

    const handlePrevious = () => {
      if (currentStep > 1) {
        setCurrentStep(currentStep - 1);
        fundraiserStore.currentBuilderStep = currentStep - 1;
        // Scroll to top when navigating to previous step
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    };

    const handleSaveFundraiser = async (closeAfterSave = false) => {
      const validationPassed = fundraiserStore.builderData.validate(
        false,
        fundraiserStore.fundraisers,
        setCurrentStep,
        navigateToSection
      );

      if (!validationPassed) {
        fundraiserStore.setBuilderData(fundraiserStore.builderData);
        return;
      }

      if (!canManageFundraiser(fundraiserId ? "Edit" : "Create")) return;
      setIsSaving(true);

      try {
        const id = await fundraiserStore.saveBuilder();

        if (!id) {
          ToastService.showError(
            fundraiserId
              ? "Failed to update fundraiser."
              : "Failed to save fundraiser."
          );
          return;
        }

        ToastService.showSuccess(
          fundraiserId
            ? "Fundraiser updated successfully!"
            : "Fundraiser created successfully!"
        );

        if (fundraiserId) {
          fundraiserStore.setSelectedFundraiser(fundraiserId);
          if (closeAfterSave) {
            navigate(`/fundraiser/${fundraiserId}`);
          }
        } else {
          fundraiserStore.setSelectedFundraiser(id);
          navigate(`/fundraiser/${id}`);
        }
      } finally {
        setIsSaving(false);
      }
    };

    const handleSave = () => handleSaveFundraiser(false);
    const handleSaveAndClose = () => handleSaveFundraiser(true);

    const navigateToSection = (
      section: BuilderSectionId,
      shouldScroll: boolean
    ) => {
      fundraiserStore.setShouldScrollToMediaSection(false);
      if (section === "media") {
        fundraiserStore.setShouldScrollToMediaSection(shouldScroll);
      } else if (section === "impact") {
        fundraiserStore.setShouldScrollToImpactSection(shouldScroll);
      } else if (section === "stats") {
        fundraiserStore.setShouldScrollToStatsSection(shouldScroll);
      }
    };

    const handlePreview = () => {
      if (fundraiserId) {
        fundraiserStore.setShowSuccessToast(false);
        fundraiserStore.setSelectedFundraiser(fundraiserId);
        navigate(`/fundraiser/${fundraiserId}`);
      }
    };

    const handleDelete = () => {
      if (fundraiserId) {
        fundraiserStore.deleteFundraiser(fundraiserId);
        // fundraiserStore.setView("dashboard");
        navigate("/fundraiser/dashboard");
      }
    };

    const isDeleteEnabled = hasPayments
      ? deleteConfirmName === fundraiserStore.builderData?.title &&
        deleteAgreement
      : true;

    const showSaveInFooter = isEditing || currentStep === 4;

    const renderStep = () => {
      switch (currentStep) {
        case 1:
          return (
            <DetailsStep
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
            />
          );
        case 2:
          return <DonationStep shouldShowError={() => true} />;
        case 3:
          return <DesignStep />;
        case 4:
          return <PromotersStep />;
        default:
          return (
            <DetailsStep
              currentStep={currentStep}
              setCurrentStep={setCurrentStep}
            />
          );
      }
    };

    const isStepCompleted = (stepNumber: number) => {
      return stepNumber <= currentStep;
    };

    const updateBuilderFields = (
      field: keyof CreateFundraiserModel,
      value: any,
      libraryField: LibraryField
    ) => {
      fundraiserStore.setBuilderData({
        ...fundraiserStore.builderData,
        [field]: value,
        [libraryField]: false,
      });
    };

    const libraryFields: LibraryField[] = [
      "showMainSectionLibrary",
      "showMediaSectionLibrary",
      "showCallToActionSectionLibrary",
      "showMarketingSectionLibrary",
    ];

    const activeLibrary = libraryFields.find(
      (field) => fundraiserStore.builderData[field] === true
    );

    const handleSelect = (media: MediaItem) => {
      const mediaModel = MediaModel.mediaItemToMediaModel(media);

      if (activeLibrary === "showMediaSectionLibrary") {
        const existing = fundraiserStore.builderData.mediaItems.some(
          (item) => item.mediaId === mediaModel.mediaId
        );

        if (existing) {
          ToastService.showError("This media is already added!");
          return;
        }
      }

      switch (activeLibrary) {
        case "showMainSectionLibrary":
          updateBuilderFields("fundraiserImage", mediaModel, activeLibrary);
          break;

        case "showMediaSectionLibrary":
          updateBuilderFields(
            "mediaItems",
            [...fundraiserStore.builderData.mediaItems, mediaModel],
            activeLibrary
          );
          break;

        case "showCallToActionSectionLibrary":
          updateBuilderFields("ctaImage", mediaModel, activeLibrary);
          break;

        case "showMarketingSectionLibrary":
          updateBuilderFields("bannerImage", mediaModel, activeLibrary);
          break;
      }
    };

    const allowOnlyOne =
      activeLibrary === "showMainSectionLibrary" ||
      activeLibrary === "showCallToActionSectionLibrary";

    if (activeLibrary) {
      return (
        <div style={{ height: "100vh", width: "100vw" }}>
          <FullLibraryView
            onSelectLibraryMedia={handleSelect}
            toggleLibrary={(show: boolean) =>
              fundraiserStore.updateBuilderField(activeLibrary, show)
            }
            allowOnlyOne={allowOnlyOne}
          />
        </div>
      );
    }

    if (showLoader) {
      return (
        <BuilderContainer>{new Loader(true).loaderComponent}</BuilderContainer>
      );
    }
    return (
      <BuilderContainer>
        <HeaderContainer isScrolled={isScrolled}>
          <Header>
            <LeftSection>
              <BackButton onClick={handleBackClick}>
                <ArrowLeft size={20} />
              </BackButton>
              <Title>
                {isEditing ? "Edit Fundraiser" : "Create New Fundraiser"}
              </Title>
            </LeftSection>

            <ActionButtons>
              {!isEditing && (
                <ActionButton
                  onClick={() =>
                    fundraiserStore.resetBuilder(AuthService.shortUniqueId)
                  }
                >
                  <RotateCcw size={18} />
                  Reset
                </ActionButton>
              )}
              {isEditing && currentStep !== 1 && (
                <ActionButton onClick={handlePreview}>
                  <Heart size={18} />
                  Donate
                </ActionButton>
              )}
              <ActionButton onClick={handleSave} disabled={isSaving}>
                <Save size={18} />
                Save
              </ActionButton>
              <ActionButton
                variant="primary"
                onClick={handleSaveAndClose}
                disabled={isSaving}
              >
                <Save size={18} />
                Save & Close
              </ActionButton>
            </ActionButtons>
          </Header>
        </HeaderContainer>

        <ContentWrapper>
          <FormCard>
            <StepIndicator>
              <StepWrapper>
                {steps.map((step, index) => (
                  <React.Fragment key={step.id}>
                    <Step
                      active={step.id === currentStep}
                      completed={isStepCompleted(
                        step.id
                      )} /* Changed to reduce opacity for completed steps */
                      clickable={true}
                      onClick={() => {
                        setCurrentStep(step.id);
                        fundraiserStore.currentBuilderStep = step.id;
                      }}
                    >
                      <StepNumber
                        active={step.id === currentStep}
                        completed={isStepCompleted(step.id)}
                      >
                        {step.id}
                      </StepNumber>
                      <StepLabel
                        active={step.id === currentStep}
                        completed={isStepCompleted(step.id)}
                      >
                        {step.label}
                      </StepLabel>
                    </Step>
                    {index < steps.length - 1 && (
                      <StepConnector completed={isStepCompleted(step.id + 1)} />
                    )}
                  </React.Fragment>
                ))}
              </StepWrapper>
            </StepIndicator>

            {renderStep()}
          </FormCard>

          <Navigation>
            <NavigationLeftSection>
              {(showSaveInFooter || currentStep === 4) && (
                <NavButton onClick={handleBackClick}>
                  <X size={16} />
                  Close
                </NavButton>
              )}
            </NavigationLeftSection>

            <CenterSection>
              {currentStep > 1 && (
                <NavButton onClick={handlePrevious}>
                  <ArrowLeft size={16} />
                  Previous
                </NavButton>
              )}

              {currentStep < 4 && (
                <NavButton onClick={handleNext}>
                  Next
                  <ArrowRight size={16} />
                </NavButton>
              )}
            </CenterSection>

            <RightSection>
              <NavButton
                onClick={handleSave}
                disabled={isSaving}
                style={{
                  background: "rgba(255, 255, 255, 0.9)",
                  color: "#64748b",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                }}
              >
                <Save size={16} />
                Save
              </NavButton>
              <NavButton
                onClick={handleSaveAndClose}
                disabled={isSaving}
                style={{
                  background: "linear-gradient(135deg, #10b981, #059669)",
                  color: "#ffffff",
                  boxShadow: "0 4px 16px rgba(16, 185, 129, 0.3)",
                }}
              >
                <Save size={16} />
                Save & Close
              </NavButton>
            </RightSection>
          </Navigation>
        </ContentWrapper>

        <UnsavedChangesModal visible={showUnsavedChangesModal}>
          <UnsavedChangesContent>
            <UnsavedChangesHeader>
              <UnsavedChangesTitle>Unsaved Changes</UnsavedChangesTitle>
            </UnsavedChangesHeader>

            <UnsavedChangesCloseButton onClick={closeUnsavedChangesModal}>
              <X size={20} />
            </UnsavedChangesCloseButton>

            <UnsavedChangesText>
              You have made changes to this fundraiser that haven't been saved
              yet. What would you like to do?
            </UnsavedChangesText>

            <UnsavedChangesActions>
              <UnsavedChangesButton
                variant="danger"
                onClick={handleUnsavedChangesContinue}
              >
                Do Not Save
              </UnsavedChangesButton>
              {isEditing && (
                <UnsavedChangesButton
                  variant="warning"
                  onClick={handleUnsavedChangesEdit}
                >
                  Edit
                </UnsavedChangesButton>
              )}
              <UnsavedChangesButton
                variant="primary"
                onClick={handleUnsavedChangesSave}
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save"}
              </UnsavedChangesButton>
            </UnsavedChangesActions>
          </UnsavedChangesContent>
        </UnsavedChangesModal>

        <Modal visible={showDeleteModal}>
          <ModalContent>
            <ModalHeader>
              <ModalTitle>Delete Fundraiser</ModalTitle>
            </ModalHeader>

            <CloseButton
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteConfirmName("");
                setDeleteAgreement(false);
              }}
            >
              <X size={20} />
            </CloseButton>

            <ModalText>
              Are you sure you want to delete this fundraiser?
            </ModalText>

            {hasPayments && (
              <>
                <WarningText>
                  This fundraiser has received payments. Deleting it will
                  permanently remove all donation history and cannot be
                  reactivated.
                </WarningText>

                <FormGroup>
                  <FormLabel>
                    Type <strong>{fundraiserStore.builderData?.title}</strong>{" "}
                    below exactly as it appears.
                  </FormLabel>
                  <DeleteConfirmInput
                    hasError={
                      deleteConfirmName.length > 0 &&
                      deleteConfirmName !== fundraiserStore.builderData?.title
                    }
                    type="text"
                    value={deleteConfirmName}
                    onChange={(e) => setDeleteConfirmName(e.target.value)}
                    placeholder="Enter fundraiser title"
                  />
                </FormGroup>

                <FormGroup>
                  <CheckboxContainer>
                    <Checkbox
                      type="checkbox"
                      checked={deleteAgreement}
                      onChange={(e) => setDeleteAgreement(e.target.checked)}
                    />
                    I understand that this fundraiser will be deleted and cannot
                    be reactivated.
                  </CheckboxContainer>
                </FormGroup>
              </>
            )}

            <ModalActions>
              <ModalButton
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmName("");
                  setDeleteAgreement(false);
                }}
              >
                Cancel
              </ModalButton>
              <ModalButton
                variant="danger"
                onClick={handleDelete}
                disabled={!isDeleteEnabled}
              >
                Delete Fundraiser
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </Modal>
      </BuilderContainer>
    );
  }
);

export default withRouter(FundraiserBuilder);
